import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AuthProvider, useAuth } from './context/AuthContext';
import BreadcrumbProvider from './context/BreadcrumbProvider';
import { setNavigate } from './services/authBridge';

// Layout
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

function lazyWithReload(factory) {
  return React.lazy(() =>
    factory().catch((error) => {
      const key = 'chunkReloadAt';
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(key, String(Date.now()));
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    })
  );
}

const Dashboard = lazyWithReload(() => import('./pages/Dashboard'));
const TravelRequests = lazyWithReload(() => import('./pages/TravelRequests'));
const TravelRequestDetail = lazyWithReload(() => import('./pages/TravelRequestDetail'));
const TravelerProfile = lazyWithReload(() => import('./pages/TravelerProfile'));
const NewTravelRequest = lazyWithReload(() => import('./pages/NewTravelRequest'));
const Approvals = lazyWithReload(() => import('./pages/Approvals'));
const Travelers = lazyWithReload(() => import('./pages/Travelers'));
const Settings = lazyWithReload(() => import('./pages/Settings'));
const AllTravel = lazyWithReload(() => import('./pages/AllTravel'));
const MyTeamTravel = lazyWithReload(() => import('./pages/MyTeamTravel'));
const MyTeamUsers = lazyWithReload(() => import('./pages/MyTeamUsers'));
const MyAssignees = lazyWithReload(() => import('./pages/MyAssignees'));
const RoomBlocks = lazyWithReload(() => import('./pages/RoomBlocks'));
const HotelRateLookup = lazyWithReload(() => import('./pages/HotelRateLookup'));
const Departments = lazyWithReload(() => import('./pages/Departments'));
const LocationManager = lazyWithReload(() => import('./pages/LocationManager'));
const HotelDetail = lazyWithReload(() => import('./pages/HotelDetail'));
const FinancialReporting = lazyWithReload(() => import('./pages/FinancialReporting'));
const EventAttendees = lazyWithReload(() => import('./pages/EventAttendees'));
const EventTravel = lazyWithReload(() => import('./pages/EventTravel'));
const SystemStats = lazyWithReload(() => import('./pages/SystemStats'));
const ReportedIssues = lazyWithReload(() => import('./pages/ReportedIssues'));
const ReportedIssueDetail = lazyWithReload(() => import('./pages/ReportedIssueDetail'));
const Changelog = lazyWithReload(() => import('./pages/Changelog'));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="spinner size-12 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading…</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // react-doctor-disable-next-line react-doctor/no-derived-state-effect
  useEffect(() => { setNavigate(navigate); }, [navigate]);

  if (loading) {
    return <RouteFallback />;
  }

  return (
    <React.Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" replace /> : <ResetPassword />} />

      <Route path="/" element={<ProtectedRoute><BreadcrumbProvider><Layout /></BreadcrumbProvider></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Travel Requests */}
        <Route path="requests" element={<TravelRequests />} />
        <Route path="requests/new" element={<ErrorBoundary><NewTravelRequest /></ErrorBoundary>} />
        <Route path="requests/:id" element={<TravelRequestDetail />} />
        <Route path="requests/:id/edit" element={<ErrorBoundary><NewTravelRequest /></ErrorBoundary>} />

        {/* Approvals - for approvers */}
        <Route
          path="approvals"
          element={
            <ProtectedRoute requiredRoles={['travel lead', 'admin']}>
              <Approvals />
            </ProtectedRoute>
          }
        />

        {/* My Assignees - for travel leads */}
        <Route
          path="my-assignees"
          element={
            <ProtectedRoute requiredRoles={['travel lead', 'admin']}>
              <MyAssignees />
            </ProtectedRoute>
          }
        />

        {/* Department Manager - Team Views */}
        <Route path="team-travel" element={<MyTeamTravel />} />
        <Route path="team-members" element={<MyTeamUsers />} />

        {/* Traveler Profiles */}
        <Route path="profile" element={<TravelerProfile />} />
        <Route
          path="travelers"
          element={
            <ProtectedRoute requiredRoles={['coordinator', 'admin']}>
              <Travelers />
            </ProtectedRoute>
          }
        />
        <Route path="travelers/:id" element={<TravelerProfile />} />

        {/* Settings */}
        <Route
          path="settings"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <Settings />
            </ProtectedRoute>
          }
        />

        {/* All Travel - Coordinators/Admin */}
        <Route
          path="all-travel"
          element={
            <ProtectedRoute requiredRoles={['admin', 'coordinator']}>
              <AllTravel />
            </ProtectedRoute>
          }
        />

        {/* Room Blocks - Coordinators */}
        <Route
          path="room-blocks"
          element={
            <ProtectedRoute requiredRoles={['coordinator', 'admin']}>
              <RoomBlocks />
            </ProtectedRoute>
          }
        />

        {/* Hotel Rate Lookup - Coordinators/Admin */}
        <Route
          path="hotel-lookup"
          element={
            <ProtectedRoute requiredRoles={['coordinator', 'admin']}>
              <HotelRateLookup />
            </ProtectedRoute>
          }
        />

        {/* Departments - Admin only */}
        <Route
          path="departments"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <Departments />
            </ProtectedRoute>
          }
        />

        {/* Location Manager - Coordinators/Admin */}
        <Route
          path="locations"
          element={
            <ProtectedRoute requiredRoles={['coordinator', 'admin']}>
              <LocationManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="locations/hotels/:id"
          element={
            <ProtectedRoute requiredRoles={['coordinator', 'admin']}>
              <HotelDetail />
            </ProtectedRoute>
          }
        />

        {/* Financial Reporting - Admin only */}
        <Route
          path="financial"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FinancialReporting />
            </ProtectedRoute>
          }
        />

        {/* System Stats - Admin only */}
        <Route
          path="system-stats"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <SystemStats />
            </ProtectedRoute>
          }
        />

        {/* Event Attendees - All users */}
        <Route path="event-attendees" element={<EventAttendees />} />

        {/* Event Travel - Admin/Coordinator */}
        <Route
          path="event-travel"
          element={
            <ProtectedRoute requiredRoles={['admin', 'coordinator']}>
              <EventTravel />
            </ProtectedRoute>
          }
        />

        <Route path="reported-issues" element={<ReportedIssues />} />
        <Route path="reported-issues/:number" element={<ReportedIssueDetail />} />

        <Route path="changelog" element={<Changelog />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </React.Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster
          position="top-right"
          containerStyle={{ zIndex: 99999 }}
          toastOptions={{
            duration: 4000,
            error: { duration: Infinity },
          }}
        >
          {(t) => (
            <div
              {...t.ariaProps}
              className={`${
                t.visible ? 'animate-enter' : 'animate-leave'
              } max-w-[400px] w-full bg-white rounded-xl pointer-events-auto border border-gray-200 shadow-soft-lg`}
              style={{
                opacity: t.visible ? 1 : 0,
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              <div className="p-4">
                <div className="flex items-start gap-3">
                  {t.type === 'success' && (
                    <CheckCircleIcon className="size-6 text-success-500 flex-shrink-0" />
                  )}
                  {t.type === 'error' && (
                    <XCircleIcon className="size-6 text-error-500 flex-shrink-0" />
                  )}
                  <div className="flex-1 text-sm text-gray-900">
                    {t.message}
                  </div>
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    aria-label="Dismiss notification"
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XMarkIcon className="size-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </Toaster>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </Router>
    </AuthProvider>
  );
}

export default App;
